import {
  Box,
  Column,
  Columns,
  Divider,
  Hidden,
  IconTick,
  ContentBlock,
  Stack,
  Text,
} from 'braid-design-system';
import React from 'react';

import { TextLink } from 'src/components';
import { TRACKING_CODES } from 'src/shared/constant';
import { encodeSvg } from 'src/shared/helpers';
import { useConfig } from 'src/shared/hooks';

import { RECRUITER_NETWORK_URL } from '../../../../../../shared/links';
import CTAButtonNoLinks from '../../../../branding/App/components/CTAButtonNoLinks';

import SeekLogo from './images/SeekLogo.svg';

import * as styles from './RecruiterNetwork.css';

const RecruiterNetwork = () => {
  const { site } = useConfig();
  const isAu = site === 'employer-seek-au';
  if (!isAu) {
    return null;
  }
  return (
    <ContentBlock width="medium">
      <Box
        borderRadius="large"
        boxShadow="borderNeutralLight"
        padding="large"
        width="full"
      >
        <Stack space="large">
          <Columns
            collapseBelow="desktop"
            space={{ desktop: 'xxlarge', mobile: 'large' }}
            alignY="center"
          >
            <RecruiterNetworkHeading />
            <RecruiterNetworkHeadingCTA />
          </Columns>
          <Hidden below="desktop">
            <Divider />
          </Hidden>
          <Stack space="large">
            <Box
              display="flex"
              flexDirection={{
                desktop: 'row',
                mobile: 'column',
              }}
              gap={{
                desktop: 'medium',
                mobile: 'large',
              }}
              textAlign="left"
            >
              {recruiterNetworkItemData.map((item, index) => (
                <RecruiterNetworkItem
                  key={index}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </Box>
            <RecruiterNetworkFooter />
          </Stack>
        </Stack>
      </Box>
    </ContentBlock>
  );
};

const RecruiterNetworkHeading = () => (
  <Column>
    <Stack space="medium" align={{ desktop: 'left', mobile: 'center' }}>
      <Box
        className={styles.image}
        style={{
          backgroundImage: encodeSvg(SeekLogo),
          backgroundSize: 'contain',
        }}
      />

      <Text
        align={{ desktop: 'left', mobile: 'center' }}
        size="standard"
        tone="secondary"
      >
        Connecting businesses with experienced recruiters who specialise in
        filling roles fast.
      </Text>
    </Stack>
  </Column>
);

const RecruiterNetworkHeadingCTA = () => (
  <Column width="content">
    <Stack space="none" align="center">
      <CTAButtonNoLinks
        text={{
          loggedIn: 'Go to SEEK Recruiter Network',
          loggedOut: 'Go to SEEK Recruiter Network',
        }}
        href={{
          loggedIn: RECRUITER_NETWORK_URL,
          loggedOut: RECRUITER_NETWORK_URL,
        }}
        tracking={{
          loggedIn: TRACKING_CODES.recruiter_network_link_clicked,
          loggedOut: TRACKING_CODES.sign_in_link_clicked,
        }}
        variant="ghost"
        tone="formAccent"
      />
    </Stack>
  </Column>
);

interface RecruiterNetworkItemProps {
  title: string;
  description: string;
}

const RecruiterNetworkItem = ({
  title,
  description,
}: RecruiterNetworkItemProps) => (
  <Box display="flex" flexDirection="row" gap="small">
    <Box>
      <IconTick tone="formAccent" size="standard" />
    </Box>
    <Box style={{ marginTop: '6px' }}>
      <Stack space="gutter">
        <Text size="large">
          <div className={`${styles.recruiterColHeading}`}>{title}</div>
        </Text>
        <Text>{description}</Text>
      </Stack>
    </Box>
  </Box>
);

const recruiterNetworkItemData: RecruiterNetworkItemProps[] = [
  {
    title: 'Quality assurance',
    description:
      'Pay for results, with a 90-day money-back guarantee* to ensure you’ve found the right person.',
  },
  {
    title: 'Find a recruiter fast',
    description:
      'Our platform matches you with experienced and reputable recruiters who specialise in your industry, role and location.',
  },
  {
    title: 'Specialised knowledge',
    description:
      'Recruiters bring hiring and industry expertise to help your role stand out and attract better candidates.',
  },
];

const RecruiterNetworkFooter = () => (
  <Text align={{ desktop: 'left', mobile: 'left' }} size="standard">
    *Subject to{' '}
    <TextLink
      tracking="recruiter-network-terms"
      href="https://talent.seek.com.au/recruiter-network/terms"
    >
      SEEK Recruiter Network Terms of Use
    </TextLink>
  </Text>
);

export default RecruiterNetwork;
